import { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import { chunk } from "../lib/batchedIn";
import { shiftDayKey } from "../lib/days";
import { rosterLookbackDays } from "../lib/roster";
import type { RosterData } from "../lib/rosterData";
import { mondayKey } from "../lib/weekLine";
import { boardInputsOf, boardRows } from "../lib/thisWeekBoard";
import {
  reviewQueue, writtenSets, type QAssignment, type QChart, type QSession, type QThreadRow, type ReviewMark, type ReviewQueue,
} from "../lib/reviewQueue";
import type { WeighIn } from "../lib/oneRepMax";
import { PLAN_TREE_SELECT, planSections, type PlanTreeSectionRow } from "../lib/setVerdict";
import { onReviewSent } from "../lib/reviewEvents";

/* THE REVIEW QUEUE'S READS (W82 step 2) — once, for the Dashboard AND the sidebar's count, so the two are one number.
 *
 * Everything is read through RLS as the signed-in coach, the policies the Client page's Training tab already meets
 * (`workout_sessions` → `workout_session_exercises` → `set_logs` / `form_clips` for a client of this coach; the coach's
 * own `coach_assignments` and their copies' trees). Three reads, each bounded:
 *   • the ACTIVE clients' sessions over the roster's look-back (`rosterLookbackDays`) — the queue needs 14 days, a PR
 *     needs the history before them, and 90 days is the bound every roster label is already worded to survive;
 *   • the movement names (the coach's library rows, else the engine's);
 *   • the window's training assignments with their trees, for "written".
 *   • the CONVERSATIONS (W82 step 4 + §8(6)): every `coach_messages` row of the active clients over the look-back — the
 *     `review` rows are the marks (a finished day with one is reviewed and leaves the queue and the sidebar's count; its
 *     time feeds `Reviewed today`, its `read_at` says seen); every row feeds REPLIES WAITING (an exercise thread whose last
 *     word is the client's). Under `coach_messages_select` (the two parties).
 * A failed read is a failed queue, never an empty one: an empty queue says "nothing to review", which would be a lie.
 */

type Raw = {
  sessions: QSession[];
  assignments: QAssignment[];
  marks: ReviewMark[];
  names: Record<string, string>;
  threads: QThreadRow[];
  charts: Record<string, QChart>;
  weighIns: Record<string, WeighIn[]>;
};

export type QueueState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; queue: ReviewQueue };

type LogRow = { status: string; completed_at: string | null; rpe: number | string | null; note: string | null; set_number: number | null; reps_completed: number | null; weight_kg: number | string | null; hold_seconds: number | null; assist_kg?: number | string | null; modifier_label_override?: string | null;
  distance_m?: number | string | null; rounds?: number | null };
type WseRow = { id: string; position: number | null; section_role: string | null; coach_exercise_id: string | null; exercise_id: string | null; set_logs: LogRow[] | null; form_clips: { id: string }[] | null };
type SessionRow = { id: string; user_id: string; kind: string; completed_at: string | null; scheduled_date: string | null; created_at: string | null; workout_session_exercises: WseRow[] | null };
type TreeRow = { name: string | null; kind: string | null; coach_workout_sections: PlanTreeSectionRow[] | null };
type AssignmentRow = { client_id: string | null; scheduled_date: string | null; coach_workouts: TreeRow | TreeRow[] | null };

const num = (v: number | string | null): number | null => {
  if (v === null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

export function useReviewQueue(data: RosterData | null, proposals: Readonly<Record<string, number>>, today: Date): QueueState {
  // ONE instant per load — every wait and the window are measured from it, so no two rows disagree across a minute.
  const [instant] = useState(() => Date.now());
  // `ht:review-sent` (F6): a review or a reply just landed — read again, without waiting for a navigation.
  const [sentSeq, setSentSeq] = useState(0);
  useEffect(() => onReviewSent(() => setSentSeq((n) => n + 1)), []);
  const [raw, setRaw] = useState<{ status: "loading" } | { status: "error"; message: string } | { status: "ready"; raw: Raw }>({ status: "loading" });

  useEffect(() => {
    if (data === null) return;
    let cancelled = false;
    void (async () => {
      const ids = data.rows.filter((r) => r.status === "active" && r.clientId !== null).map((r) => r.clientId as string);
      if (ids.length === 0) { if (!cancelled) setRaw({ status: "ready", raw: { sessions: [], assignments: [], marks: [], names: {}, threads: [], charts: {}, weighIns: {} } }); return; }
      const fail = (message: string) => { if (!cancelled) setRaw({ status: "error", message }); };
      const lookback = shiftDayKey(today, -rosterLookbackDays);
      const windowKey = mondayKey(today, -1);

      const sessionRows: SessionRow[] = [];
      for (const part of chunk(ids)) {
        const r = await supabase.from("workout_sessions")
          .select("id, user_id, kind, completed_at, scheduled_date, created_at, workout_session_exercises(id, position, section_role, coach_exercise_id, exercise_id, set_logs(status, completed_at, rpe, note, set_number, reps_completed, weight_kg, hold_seconds, assist_kg, modifier_label_override, distance_m, rounds), form_clips(id))")
          .in("user_id", part)
          .gte("created_at", lookback);
        if (cancelled) return;
        if (r.error !== null) return fail(r.error.message);
        sessionRows.push(...((r.data ?? []) as unknown as SessionRow[]));
      }

      const wses = sessionRows.flatMap((s) => s.workout_session_exercises ?? []);
      const coachIds = [...new Set(wses.map((w) => w.coach_exercise_id).filter((x): x is string => x !== null))];
      const engineIds = [...new Set(wses.map((w) => w.exercise_id).filter((x): x is string => x !== null))];
      const names = new Map<string, string>();
      // 🔴 W125-F2: a PR is judged on the field the chart OPENS on, so the queue reads each coach lift's chart settings on the
      // same read as its name. Every coach key starts OFF (the column's default, and what a failed read must mean): a PR is
      // never flagged on an estimate nobody can see.
      const charts: Record<string, QChart> = {};
      for (const id of coachIds) charts[id] = { progressViews: [], oneRM: { kind: "coach", enabled: false, bodyIsLoad: false } };
      for (const part of chunk(coachIds)) {
        const r = await supabase.from("coach_exercises").select("id, name, progress_views, one_rm_enabled, body_is_load").in("id", part);
        if (cancelled) return;
        for (const e of r.data ?? []) {
          names.set(e.id, e.name);
          charts[e.id] = { progressViews: e.progress_views ?? [],
            oneRM: { kind: "coach", enabled: e.one_rm_enabled === true, bodyIsLoad: e.body_is_load === true } };
        }
      }
      // W125-F2: the clients' weigh-ins (`body_measurements_coach_read`) — a 1RM with the body as load needs one; none = no
      // 1RM point, so no 1RM PR. A failed read is the same empty list (the chart says so; the queue claims nothing).
      const weighIns: Record<string, WeighIn[]> = {};
      for (const part of chunk(ids)) {
        const r = await supabase.from("body_measurements").select("user_id, measured_at, weight_kg").in("user_id", part);
        if (cancelled) return;
        for (const m of (r.data ?? []) as { user_id: string; measured_at: string | null; weight_kg: number | string | null }[]) {
          const kg = num(m.weight_kg), at = m.measured_at === null ? NaN : Date.parse(m.measured_at.length === 10 ? `${m.measured_at}T12:00:00` : m.measured_at);
          if (kg !== null && kg > 0 && Number.isFinite(at)) (weighIns[m.user_id] ??= []).push({ at, kg });
        }
      }
      for (const part of chunk(engineIds)) {
        const r = await supabase.from("exercises").select("id, name").in("id", part);
        if (cancelled) return;
        for (const e of r.data ?? []) names.set(e.id, e.name);
      }

      const assignmentRows: AssignmentRow[] = [];
      for (const part of chunk(ids)) {
        const r = await supabase.from("coach_assignments")
          .select(`client_id, scheduled_date, coach_workouts!workout_id(name, kind, ${PLAN_TREE_SELECT})`)
          .in("client_id", part)
          .gte("scheduled_date", windowKey);
        if (cancelled) return;
        if (r.error !== null) return fail(r.error.message);
        assignmentRows.push(...((r.data ?? []) as unknown as AssignmentRow[]));
      }

      const sessions: QSession[] = sessionRows.map((s) => ({
        id: s.id, userId: s.user_id, kind: s.kind, completedAt: s.completed_at, scheduledDate: s.scheduled_date, createdAt: s.created_at,
        exercises: (s.workout_session_exercises ?? []).map((w) => {
          const key = w.coach_exercise_id ?? w.exercise_id;
          return {
            id: w.id, position: w.position, section_role: w.section_role, key,
            name: key === null ? "Unnamed exercise" : (names.get(key) ?? "Unnamed exercise"),
            clips: (w.form_clips ?? []).length,
            logs: (w.set_logs ?? []).map((l) => ({ ...l, rpe: num(l.rpe), weight_kg: num(l.weight_kg), assist_kg: num(l.assist_kg ?? null), distance_m: num(l.distance_m ?? null) })),
          };
        }),
      }));
      const assignments: QAssignment[] = [];
      for (const a of assignmentRows) {
        // ⚠️ An embed comes back as OBJECT or ARRAY by inferred relationship (the codex) — both are read.
        const w = Array.isArray(a.coach_workouts) ? (a.coach_workouts[0] ?? null) : a.coach_workouts;
        if (a.client_id === null || a.scheduled_date === null || w === null || w.kind === "task") continue;
        // The whole tree (`setVerdict.planSections`): its written count, and the numbers `as written` is judged on.
        const sections = planSections(w.coach_workout_sections);
        assignments.push({ clientId: a.client_id, dayKey: a.scheduled_date.slice(0, 10), workoutName: w.name, written: writtenSets(sections), sections });
      }
      // The plan's own exercises may never have been touched by the record (the watch's `not logged` names them).
      const planIds = [...new Set(assignments.flatMap((a) => (a.sections ?? []).flatMap((s) => s.exercises.map((e) => e.exercise_id)))
        .filter((x): x is string => x !== null && !names.has(x)))];
      for (const part of chunk(planIds)) {
        const r = await supabase.from("coach_exercises").select("id, name").in("id", part);
        if (cancelled) return;
        for (const e of r.data ?? []) names.set(e.id, e.name);
      }
      // ONE read of the conversations: the review rows are the marks; every row feeds the replies-waiting rule (§8(6)).
      const marks: ReviewMark[] = [];
      const threads: QThreadRow[] = [];
      for (const part of chunk(ids)) {
        const r = await supabase.from("coach_messages")
          .select("id, kind, body, session_id, exercise_row_id, reply_to, sender_id, coach_id, client_id, created_at, read_at")
          .in("client_id", part).gte("created_at", lookback);
        if (cancelled) return;
        if (r.error !== null) return fail(r.error.message);
        for (const m of r.data ?? []) {
          if (m.kind === "review") marks.push({ id: m.id, kind: m.kind, session_id: m.session_id, created_at: m.created_at, read_at: m.read_at });
          threads.push({ id: m.id, kind: m.kind, body: m.body, sessionId: m.session_id, exerciseRowId: m.exercise_row_id, replyTo: m.reply_to,
            createdAtISO: m.created_at, clientId: m.client_id, fromCoach: m.sender_id === m.coach_id });
        }
      }
      if (!cancelled) setRaw({ status: "ready", raw: { sessions, assignments, marks, names: Object.fromEntries(names), threads, charts, weighIns } });
    })();
    return () => { cancelled = true; };
  }, [data, today, sentSeq]);

  return useMemo<QueueState>(() => {
    if (data === null || raw.status === "loading") return { status: "loading" };
    if (raw.status === "error") return raw;
    return {
      status: "ready",
      queue: reviewQueue({
        rows: boardRows(boardInputsOf(data, proposals)), ...raw.raw, now: instant, today,
      }),
    };
  }, [data, proposals, raw, today, instant]);
}
